import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserCheck,
  FaLaptopCode,
  FaChartLine,
  FaShieldAlt,
  FaPhone,
  FaBook,
  FaRocket,
  FaCheckCircle,
} from "react-icons/fa";
import { motion, useScroll, useTransform } from "framer-motion";


const modules = [
  { title: "Account Setup", desc: "Complete setup with best practices.", icon: FaUserCheck },
  { title: "Product Listing", desc: "Single & bulk listing mastery.", icon: FaLaptopCode },
  { title: "Category Approval", desc: "Unlock restricted categories.", icon: FaShieldAlt },
  { title: "Brand Registry", desc: "Protect and grow your brand.", icon: FaShieldAlt },
  { title: "GTIN Approval", desc: "Barcode & exemption process.", icon: FaCheckCircle },
  { title: "SEO Optimization", desc: "Rank products using powerful keywords.", icon: FaChartLine },
  { title: "Image Enhancement", desc: "High-converting product visuals.", icon: FaLaptopCode },
  { title: "Pricing Strategy", desc: "Win Buy Box & improve margins.", icon: FaChartLine },
  { title: "A+ Content", desc: "Premium product storytelling pages.", icon: FaBook },
  { title: "Brand Store", desc: "Modern storefront experience.", icon: FaRocket },
  { title: "Amazon Ads", desc: "Scalable PPC campaigns.", icon: FaChartLine },
  { title: "FBA Training", desc: "End-to-end logistics mastery.", icon: FaRocket },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.7, ease: "easeOut" },
  }),
};

export default function AmazonTraining() {

  const navigate = useNavigate();

  const { scrollY } = useScroll();

  const glow1 = useTransform(scrollY, [0, 600], [0, -120]);
  const glow2 = useTransform(scrollY, [0, 600], [0, 160]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const styles = {
    page: {
      background: "#0b0f17",
      color: "white",
      overflowX: "hidden",
      fontFamily: "system-ui",
    },

    hero: {
      height: "60vh",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "0 20px",
    },

    glow: {
      position: "absolute",
      width: "500px",
      height: "500px",
      borderRadius: "50%",
      background: "rgba(99,102,241,0.25)",
      filter: "blur(120px)",
    },

    glow2: {
      position: "absolute",
      width: "500px",
      height: "500px",
      borderRadius: "50%",
      background: "rgba(236,72,153,0.18)",
      filter: "blur(140px)",
    },

    heroTitle: {
      fontSize: "60px",
      fontWeight: 600,
      letterSpacing: "-1px",
    },

    heroText: {
      fontSize: "18px",
      opacity: 0.65,
      maxWidth: "600px",
      margin: "20px auto 0",
      lineHeight: "1.6",
    },

    button: {
      marginTop: "30px",
      padding: "14px 28px",
      borderRadius: "999px",
      background: "white",
      color: "black",
      border: "none",
      fontWeight: 500,
      cursor: "pointer",
    },

    section: {
      padding: "20px",
      maxWidth: "1100px",
      margin: "0 auto",
    },

    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: "20px",
    },

    card: {
      padding: "24px",
      borderRadius: "18px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.03)",
      backdropFilter: "blur(10px)",
    },

    iconBox: {
      width: "44px",
      height: "44px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255,255,255,0.06)",
      marginBottom: "14px",
    },

    title: {
      fontSize: "16px",
      fontWeight: 500,
    },

    desc: {
      fontSize: "13px",
      opacity: 0.6,
      marginTop: "6px",
      lineHeight: "1.5",
    },

    cta: {
      textAlign: "center",
      padding: "60px",
      borderRadius: "24px",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
    },

    phone: {
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      marginTop: "20px",
      padding: "14px 26px",
      borderRadius: "999px",
      background: "white",
      color: "black",
      textDecoration: "none",
      fontWeight: 500,
    },
  };

  return (
    <div style={styles.page}>

      {/* HERO */}
      <motion.section style={{ ...styles.hero, opacity: heroOpacity }}>

        <motion.div style={{ ...styles.glow, x: glow1, top: "-100px", left: "-100px" }} />
        <motion.div style={{ ...styles.glow2, x: glow2, bottom: "-120px", right: "-120px" }} />

        <div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={styles.heroTitle}
          >
            Amazon Training
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            style={styles.heroText}
          >
            Learn Amazon like a system — not theory.
          </motion.p>
<button
  type="button"
  className="btn"
  onClick={() => window.location.href = "/contact"}
>
  Book Free Consultation
</button>

        </div>
      </motion.section>

      {/* MODULES */}
      <section style={styles.section}>
        <div style={styles.grid}>
          {modules.map((item, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              style={styles.card}
            >
              <div style={styles.iconBox}>
                <item.icon />
              </div>

              <div style={styles.title}>{item.title}</div>
              <div style={styles.desc}>{item.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={styles.section}>
        <div style={styles.cta}>
          <h2 style={{ fontSize: "28px", fontWeight: 600 }}>
            Start Your Amazon Journey
          </h2>

          <p style={{ opacity: 0.6, marginTop: "10px", fontSize: "13px" }}>
            Get expert guidance and scale faster
          </p>

          <a href="tel:8527203697" style={styles.phone}>
            <FaPhone />
            Call Now
          </a>
        </div>
      </section>

    </div>
  );
}